import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getClients,
  getClientOverview,
  getClientSubscription,
  getClientActivityLog,
  getClientTokens,
  revokeClientToken,
  deactivateClient,
} from "./client.api";
import type { ActivityLogParams } from "../types/client.types";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";

export const useGetClients = () => {
  return useQuery({
    queryKey: ["clients"],
    queryFn: getClients,
  });
};

export const useGetClientOverview = (clientId: string) => {
  return useQuery({
    queryKey: ["client-overview", clientId],
    queryFn: () => getClientOverview(clientId),
  });
};

export const useGetClientSubscription = (userId: string) => {
  return useQuery({
    queryKey: ["client-subscription", userId],
    queryFn: () => getClientSubscription(userId),
  });
};

export const useGetClientActivityLog = ({
  userId,
  page = 1,
  pageSize = 50,
}: ActivityLogParams) => {
  return useQuery({
    queryKey: ["client-activity-log", userId, page, pageSize],
    queryFn: () => getClientActivityLog({ userId, page, pageSize }),
    placeholderData: (previousData) => previousData,
  });
};

export const useGetClientTokens = (userId: string) => {
  return useQuery({
    queryKey: ["client-tokens", userId],
    queryFn: () => getClientTokens(userId),
  });
};

export const useRevokeClientToken = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["revoke-client-token", userId],
    mutationFn: (apiKeyId: string) => revokeClientToken(userId, apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-tokens", userId] });
      toast.success("API key revoked successfully");
    },
    onError: (error) => {
      toast.error("Failed to revoke API key", {
        description: extractErrorMessage(error),
      });
      queryClient.invalidateQueries({ queryKey: ["client-tokens", userId] });
    },
  });
};

export const useDeactivateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deactivate-client"],
    mutationFn: (userId: string) => deactivateClient(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
    onError: (error) => {
      toast.error("Failed to deactivate client", {
        description: extractErrorMessage(error),
      });
    },
  });
};
